import React, { useMemo, useRef, useState } from "react";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import styles from "./dashboard-edit-widget-drawer-dropdown.module.scss";
import clearIcon from "@src/assets/svg/close-icon.svg";
import { NameAndSourceId } from "@common/types/server-list-types";
import SpinnerWithVariant from "../SpinnerWithVariant/spinner-with-variant";

const cssClassName = "dashboard-edit-widget-drawer-dropdown";

type DropdownOption = NameAndSourceId & {
  searchText?: string;
};

interface DashboardEditWidgetDrawerDropdownProps {
  options: DropdownOption[];
  selectedOption: DropdownOption | null;
  onSelectOption: (option: DropdownOption | null) => void;
  /** Omitted when the caller renders its own label above the control (e.g.
   *  next to a toggle) — an empty label would only add a blank line. */
  labelText?: string;
  noOptionsText: string;
  placeholderText: string;
  isLoading: boolean;
}

export const DashboardEditWidgetDrawerDropdown = ({
  options,
  selectedOption,
  onSelectOption,
  labelText,
  noOptionsText,
  placeholderText,
  isLoading,
}: DashboardEditWidgetDrawerDropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const closeDropdown = () => {
    setIsOpen(false);
    setInputValue("");
  };

  useClickOutsideListener(containerRef, closeDropdown, isOpen);

  const filteredOptions = useMemo(() => {
    const normalized = inputValue.trim().toLowerCase();
    if (!normalized) {
      return options;
    }

    return options.filter((option) =>
      [
        option.name,
        option.source_id,
        option.searchText,
        option.nodePath?.join(" / "),
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalized)),
    );
  }, [inputValue, options]);

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setIsOpen(true);
  };

  const handleSelect = (option: DropdownOption) => {
    onSelectOption(option);
    setInputValue("");
    setIsOpen(false);
  };

  const handleRemoveOption = () => {
    onSelectOption(null);
    setInputValue("");
    setIsOpen(true);
  };

  const renderDropdownContent = () => {
    if (isLoading) {
      return (
        <div className={styles[`${cssClassName}__loading-item`]}>
          <SpinnerWithVariant variant="small" />
        </div>
      );
    }

    if (filteredOptions.length === 0) {
      return (
        <div className={styles[`${cssClassName}__item`]}>{noOptionsText}</div>
      );
    }

    return filteredOptions.map((option) => (
      <button
        key={option.source_id}
        type="button"
        className={styles[`${cssClassName}__item`]}
        onClick={() => handleSelect(option)}
      >
        <span className={styles[`${cssClassName}__item-name`]}>
          {option.name}
        </span>
        {option.nodePath?.length ? (
          <span className={styles[`${cssClassName}__item-path`]}>
            {option.nodePath.join(" / ")}
          </span>
        ) : null}
      </button>
    ));
  };

  const renderSelectedOption = () => (
    <span className={styles[`${cssClassName}__tag`]}>
      {selectedOption?.name}
      <button
        type="button"
        className={styles[`${cssClassName}__tag-remove`]}
        onClick={handleRemoveOption}
      >
        <img
          src={clearIcon}
          alt="Remove"
          className={styles[`${cssClassName}__tag-remove-icon`]}
        />
      </button>
    </span>
  );

  const renderInput = () => (
    <input
      className={styles[`${cssClassName}__input`]}
      placeholder={placeholderText}
      value={inputValue}
      onFocus={handleInputFocus}
      onChange={(e) => handleInputChange(e.target.value)}
    />
  );

  return (
    <div className={styles[cssClassName]}>
      {labelText ? (
        <label className={styles[`${cssClassName}__label`]}>{labelText}</label>
      ) : null}

      <div className={styles[`${cssClassName}__input-wrapper`]}>
        {selectedOption ? renderSelectedOption() : renderInput()}
      </div>

      {isOpen && (
        <div ref={containerRef} className={styles[`${cssClassName}__list`]}>
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
};
